/**
 * /customer/qr — present-to-cashier QR surface (PHASE W-1).
 *
 * Mirrors mobile/app/(customer)/stamp.tsx 1:1:
 *   - POST /api/me/qr-session with the chosen customer_card_id
 *   - server returns qr_token + expires_in_seconds (60s)
 *   - client ticks at 1Hz; on remaining<=1 → re-POST same card_id
 *   - countdown progress bar visualises the time-until-rotate
 *
 * Card picker:
 *   - hidden when the user has exactly 1 card
 *   - horizontal scrollable strip otherwise
 *   - selected card_id persisted to sessionStorage so returning to the
 *     page restores the picker state
 *
 * Customer cannot add stamps via this screen — it is pure presenter.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { QRCodeSVG } from 'qrcode.react';
import { Loader2, AlertCircle } from 'lucide-react';
import { api, resolveImageUrl } from '@/lib/api';
import { qk } from '@/lib/queryKeys';
import { useAuthStore } from '@/lib/auth';

const LAST_CARD_KEY = 'onestamp.customer.last_qr_card_id';

export default function CustomerQRPage() {
  const token = useAuthStore((s) => s.token);
  const user = useAuthStore((s) => s.user);

  const cardsQ = useQuery({
    queryKey: qk.customerCards(),
    queryFn: () => api.customerCards(token),
    enabled: !!token,
  });

  const cards = useMemo(() => cardsQ.data ?? [], [cardsQ.data]);
  const [selectedCardId, setSelectedCardId] = useState(null);
  // Phase 2b — `?card=<id>` deep-link from Card Detail's "Show QR" CTA.
  // If present and valid, take precedence over sessionStorage. If
  // present but invalid (not a joined card) we surface a soft inline
  // notice and fall back to the default picker behaviour.
  const [searchParams] = useSearchParams();
  const deepLinkCardId = searchParams.get('card');
  const [deepLinkInvalid, setDeepLinkInvalid] = useState(false);

  // Pick the initial card: deep-link → stored value → first available
  useEffect(() => {
    if (!cards.length || selectedCardId) return;
    if (deepLinkCardId) {
      const match = cards.find((c) => c.id === deepLinkCardId);
      if (match) {
        setSelectedCardId(match.id);
        try {
          sessionStorage.setItem(LAST_CARD_KEY, match.id);
        } catch (_) { /* ignore */ }
        return;
      }
      // present but invalid → soft notice, then fall through to default
      setDeepLinkInvalid(true);
    }
    let stored = null;
    try {
      stored = sessionStorage.getItem(LAST_CARD_KEY);
    } catch (_) {
      // ignore
    }
    const found = stored && cards.find((c) => c.id === stored) ? stored : cards[0].id;
    setSelectedCardId(found);
  }, [cards, selectedCardId, deepLinkCardId]);

  const handleSelectCard = (cid) => {
    setSelectedCardId(cid);
    try {
      sessionStorage.setItem(LAST_CARD_KEY, cid);
    } catch (_) {
      // ignore
    }
  };

  const [session, setSession] = useState(null);
  const [remaining, setRemaining] = useState(60);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const tickRef = useRef(null);

  const issue = useCallback(async () => {
    if (!token || !selectedCardId) return;
    try {
      setError(null);
      setLoading(true);
      const s = await api.customerQrSession(token, selectedCardId);
      setSession(s);
      setRemaining(s.expires_in_seconds);
    } catch (e) {
      setError(e?.detail ?? 'Could not generate QR. Check your connection and retry.');
    } finally {
      setLoading(false);
    }
  }, [token, selectedCardId]);

  // (re)issue when card selection changes
  useEffect(() => {
    if (!selectedCardId) return;
    issue();
  }, [selectedCardId, issue]);

  // 1Hz tick; rotate at zero
  useEffect(() => {
    if (!session) return undefined;
    tickRef.current = setInterval(() => {
      setRemaining((r) => {
        if (r <= 1) {
          issue();
          return session.expires_in_seconds;
        }
        return r - 1;
      });
    }, 1000);
    return () => {
      if (tickRef.current) clearInterval(tickRef.current);
    };
  }, [session, issue]);

  const selectedCard = useMemo(
    () => cards.find((c) => c.id === selectedCardId) || null,
    [cards, selectedCardId],
  );
  const progressPct = session
    ? Math.max(0, (remaining / session.expires_in_seconds) * 100)
    : 0;

  return (
    <div className="space-y-8" data-testid="customer-qr-page">
      <div>
        <div className="text-xs uppercase tracking-widest text-gold font-mono mb-2">
          Present at counter
        </div>
        <h1 className="font-display text-3xl font-bold">Your QR code</h1>
        <p className="text-sm text-white/55 mt-1.5">
          Show this to the cashier. The code refreshes every 60 seconds.
        </p>
      </div>

      {/* Phase 2b — soft notice when the ?card= deep-link doesn't match
          any joined card. Non-blocking; user picks from the chips below. */}
      {deepLinkInvalid ? (
        <div
          className="rounded-xl border border-amber-400/35 bg-amber-500/10 text-amber-200 text-xs px-3 py-2"
          data-testid="customer-qr-deep-link-invalid"
        >
          Card not found — pick one below.
        </div>
      ) : null}

      {/* Card picker — hidden when single-card */}
      {cards.length > 1 ? (
        <div data-testid="customer-qr-card-picker">
          <div className="text-xs uppercase tracking-wider text-white/45 font-mono mb-2">
            Choose card
          </div>
          <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
            {cards.map((c) => {
              const active = c.id === selectedCardId;
              return (
                <button
                  key={c.id}
                  onClick={() => handleSelectCard(c.id)}
                  data-testid={`customer-qr-picker-${c.id}`}
                  className={
                    'shrink-0 inline-flex items-center gap-2 px-3 py-1.5 rounded-xl border text-sm transition-colors ' +
                    (active
                      ? 'border-gold/40 bg-gold/10 text-gold shadow-glow-gold-sm'
                      : 'border-white/10 bg-white/5 text-white/70 hover:bg-white/8 hover:text-white')
                  }
                >
                  {c.brand?.logo_url ? (
                    <img
                      src={resolveImageUrl(c.brand.logo_url)}
                      alt=""
                      className="w-5 h-5 rounded-md"
                    />
                  ) : null}
                  {c.brand?.name}
                </button>
              );
            })}
          </div>
        </div>
      ) : null}

      {/* QR card */}
      <div
        className="rounded-3xl border border-gold/25 bg-gradient-to-br from-[#15151B] to-[#0E0E13] shadow-glow-gold p-6 md:p-8 max-w-md mx-auto"
        data-testid="customer-qr-card"
      >
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="font-display text-lg font-semibold">
              {user?.name ?? '—'}
            </div>
            <div className="text-xs font-mono text-gold mt-0.5">
              {user?.membership_id ?? '—'}
            </div>
          </div>
          {selectedCard?.brand?.logo_url ? (
            <img
              src={resolveImageUrl(selectedCard.brand.logo_url)}
              alt=""
              className="w-12 h-12 rounded-2xl border border-white/10"
            />
          ) : null}
        </div>

        {/* QR */}
        <div className="bg-white rounded-2xl p-5 flex items-center justify-center" data-testid="customer-qr-canvas">
          {loading && !session ? (
            <div className="aspect-square w-full flex items-center justify-center">
              <Loader2 className="animate-spin text-black/40" size={32} />
            </div>
          ) : error ? (
            <div className="aspect-square w-full flex flex-col items-center justify-center gap-2 text-black/60 px-4 text-center">
              <AlertCircle size={24} className="text-rose-500" />
              <div className="text-xs">{error}</div>
              <button
                type="button"
                onClick={issue}
                className="mt-2 text-xs text-gold-700 hover:underline"
              >
                Retry
              </button>
            </div>
          ) : session ? (
            <QRCodeSVG
              value={session.qr_token}
              size={240}
              level="M"
              bgColor="#FFFFFF"
              fgColor="#000000"
              data-testid="customer-qr-svg"
            />
          ) : null}
        </div>

        {/* Brand + progress */}
        {selectedCard ? (
          <div className="mt-5 flex items-center justify-between text-sm">
            <div className="text-white/70">{selectedCard.brand?.name}</div>
            <div className="font-mono text-gold">
              {selectedCard.current_stamps ?? 0} /{' '}
              {selectedCard.next_reward_at ?? selectedCard.brand?.stamps_per_card ?? 10}
            </div>
          </div>
        ) : null}

        {/* Countdown */}
        <div className="mt-5" data-testid="customer-qr-countdown">
          <div className="h-1.5 rounded-full bg-white/8 overflow-hidden">
            <div
              className="h-full bg-gold rounded-full transition-[width] duration-1000 ease-linear"
              style={{ width: `${progressPct}%` }}
            />
          </div>
          <div className="mt-2 flex items-center justify-between text-xs">
            <span className="text-white/45">Refreshes in</span>
            <span className="font-mono text-gold" data-testid="customer-qr-countdown-seconds">
              {remaining}s
            </span>
          </div>
        </div>
      </div>

      <p className="text-center text-xs text-white/40 max-w-md mx-auto">
        Your QR code is single-use. The cashier scans it to add stamps —
        you cannot add stamps yourself.
      </p>
    </div>
  );
}
